import Base from './Base';

export type ApiDocumentsResponse = ApiListResponse<DocumentSnippet>;

export type ApiDocumentsRecommendationResponse = {
    data: DocumentSnippet[];
    recommendationId?: string;
};

class Documents extends Base {
    get(query = null, page = null, count = null, opts = null) {
        return this.getJSON<ApiDocumentsResponse>(
            '/documents',
            {
                ...(page !== null ? { page } : null),
                ...(count !== null ? { count } : null),
                ...query,
            },
            opts,
        );
    }

    next(id, query = null, count = null, opts = null) {
        return this.getJSONWithSession<ApiDocumentsRecommendationResponse>(
            `/documents/${id}/next`,
            {
                ...(count !== null ? { count } : null),
                ...query,
            },
            opts,
        );
    }

    related(id, query = null, count = null, opts = null) {
        return this.getJSONWithSession<ApiDocumentsRecommendationResponse>(
            `/documents/${id}/related`,
            {
                ...(count !== null ? { count } : null),
                ...query,
            },
            opts,
        );
    }

    progress(query = null, opts = null) {
        return this.isBot
            ? Promise.resolve({})
            : this.getJSONWithSession(
                  `/documents/progress`,
                  {
                      ...query,
                  },
                  opts,
              );
    }

    popular(query = null, count = null, opts = null) {
        return this.getJSONWithSession<ApiDocumentsRecommendationResponse>(
            `/documents/popular`,
            {
                ...(count !== null ? { count } : null),
                ...query,
            },
            opts,
        );
    }

    continue(query = null, count = null, opts = null) {
        return this.getJSONWithSession<ApiDocumentsRecommendationResponse>(
            `/documents/continue`,
            {
                ...(count !== null ? { count } : null),
                ...query,
            },
            opts,
        );
    }

    findBySlug(slug, opts = null) {
        const { snippetOnly = null } = opts || {};
        return this.getJSON<FullDocument | null>(
            '/documents',
            {
                uri: slug,
                snippet_only: snippetOnly === true ? 'true' : undefined,
            },
            opts,
        ).catch((e) => {
            if (e.status === 404) {
                return null;
            }
            throw e;
        });
    }

    findPreviewToken(preview, opts = null) {
        return this.getJSONWithSession<FullDocument | null>(
            '/documents',
            {
                preview,
            },
            opts,
        ).catch((e) => {
            if (e.status === 404) {
                return null;
            }
            throw e;
        });
    }

    getBlockData(id, blockName, opts = null) {
        return this.getJSON(`/documents/${id}/data/${blockName}`, {}, opts);
    }

    formCampaign(id: string, opts?: RequestInit): Promise<FormCampaign | null> {
        // The API answers 204 without body when no campaign targets the document
        return fetch(this.getFullUrl(`/documents/${id}/form-campaign`), {
            ...opts,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        }).then((response) => {
            if (response.status === 204) {
                return null;
            }
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            return response.json();
        });
    }
}

export default Documents;
